// Project-detail top area — 3-card layout (Beschreibung / Anhänge / Datei-
// Upload) plus a full-width Gesamtfortschritt bar below. Cards 2 and 3 split
// the attachment list and the upload drop-zone but share ONE useAttachments
// instance, so list + upload + pending state stay in sync.

import { useMemo, useState } from "preact/hooks";
import { Search } from "lucide-preact";
import type { ProjectWithCounts } from "@juuplan/shared";
import { StatusBadge } from "../ProjectCard";
import { formatDateShort } from "../../lib/format";
import { useAttachments } from "./useAttachments";
import { AttachmentDropzone, AttachmentRows } from "./AttachmentList";

interface Props {
  project: ProjectWithCounts;
  readOnly: boolean;
  /** Wired to the page's settings panel — "Bearbeiten" link in card 1. */
  onEdit: () => void;
}

// Card shell matching ProjectCard: surface-container-lowest + outline-variant.
const CARD =
  "flex h-full flex-col rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]";
const CARD_HEADER_LABEL =
  "text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]";

export function ProjectTopCards({
  project,
  readOnly,
  onEdit,
}: Props): preact.JSX.Element {
  const att = useAttachments({ projectId: project.id });
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return att.items;
    return att.items.filter((a) => a.filename.toLowerCase().includes(q));
  }, [att.items, query]);

  const total = project.tasks_total;
  const done = project.tasks_done;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const desc = project.description?.trim() ?? "";

  return (
    <div class="mb-5 flex flex-col gap-4">
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-stretch">
        {/* CARD 1 — Beschreibung */}
        <section class="lg:col-span-4">
          <div class={`${CARD} p-4`}>
            <div class="mb-3 flex items-center justify-between">
              <h2 class={CARD_HEADER_LABEL}>Beschreibung</h2>
              {!readOnly && (
                <button
                  type="button"
                  onClick={onEdit}
                  class="rounded text-xs font-semibold text-[color:var(--color-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
                >
                  Bearbeiten
                </button>
              )}
            </div>

            <DescriptionBody text={desc} />

            <div class="mt-auto grid grid-cols-2 gap-4 border-t border-[color:var(--color-outline-variant)] pt-4">
              <div>
                <span class="mb-1 block text-label font-bold uppercase text-[color:var(--color-on-surface-variant)]">
                  Status
                </span>
                <StatusBadge status={project.status} />
              </div>
              <div>
                <span class="mb-1 block text-label font-bold uppercase text-[color:var(--color-on-surface-variant)]">
                  Zieldatum
                </span>
                <span class="text-sm font-medium text-[color:var(--color-on-surface)]">
                  {formatDateShort(project.target_date)}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* CARD 2 — Anhänge (list + search) */}
        <section class="lg:col-span-5">
          <div class={`${CARD} max-h-[360px] lg:max-h-none`}>
            <div class="flex items-center justify-between gap-3 border-b border-[color:var(--color-outline-variant)] p-4">
              <div class="flex items-center gap-2">
                <h2 class={CARD_HEADER_LABEL}>Anhänge</h2>
                <span class="rounded-full bg-[color:var(--color-surface-container-high)] px-1.5 py-0.5 text-label font-bold text-[color:var(--color-on-surface-variant)]">
                  {att.totalCount}
                </span>
              </div>
              <div class="relative">
                <Search
                  size={14}
                  class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-on-surface-variant)]"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={query}
                  onInput={(e) =>
                    setQuery((e.target as HTMLInputElement).value)
                  }
                  placeholder="Suchen…"
                  aria-label="Anhänge durchsuchen"
                  class="w-28 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-1 pl-7 pr-3 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] sm:w-36"
                />
              </div>
            </div>

            {/* Max ~4 Anhänge sichtbar, ab dem 5. scrollen — hält den
                Header-Bereich kompakt, egal wie viele Anhänge ein Projekt hat. */}
            <div class="min-h-0 flex-1 overflow-y-auto p-2 max-h-[15.5rem]">
              {att.loading && att.items.length === 0 ? (
                <p class="p-2 text-xs italic text-[color:var(--color-on-surface-variant)]">
                  Lädt…
                </p>
              ) : att.totalCount === 0 ? (
                <p class="p-2 text-xs italic text-[color:var(--color-on-surface-variant)]">
                  Noch keine Anhänge.
                </p>
              ) : filtered.length === 0 ? (
                <p class="p-2 text-xs italic text-[color:var(--color-on-surface-variant)]">
                  Keine Treffer für „{query}".
                </p>
              ) : (
                <AttachmentRows
                  items={filtered}
                  // Hide in-flight rows while a search filter is active.
                  pending={query.trim() ? [] : att.pending}
                  onDelete={att.doDelete}
                />
              )}
            </div>
          </div>
        </section>

        {/* CARD 3 — Datei-Upload (drop-zone, shares state with card 2) */}
        <section class="lg:col-span-3">
          <div class={CARD}>
            <div class="border-b border-[color:var(--color-outline-variant)] p-4">
              <h2 class={CARD_HEADER_LABEL}>Datei-Upload</h2>
            </div>
            <div class="flex flex-1 flex-col p-4">
              {readOnly ? (
                <p class="m-auto text-center text-xs italic text-[color:var(--color-on-surface-variant)]">
                  Nur-Lese-Zugriff — kein Upload möglich.
                </p>
              ) : (
                <AttachmentDropzone hook={att} fill />
              )}
            </div>
          </div>
        </section>
      </div>

      {/* GESAMTFORTSCHRITT — full width */}
      <div class={`${CARD} p-4`}>
        <div class="mb-2 flex items-center justify-between">
          <span class={CARD_HEADER_LABEL}>Gesamtfortschritt</span>
          <span class="text-xs font-semibold text-[color:var(--color-on-surface)]">
            {done} / {total} ({pct}%)
          </span>
        </div>
        <div class="h-2 overflow-hidden rounded-full bg-[color:var(--color-surface-container-low)]">
          <div
            class="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              background: project.color ?? "var(--color-primary)",
            }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

// Beschreibungstext mit „Mehr anzeigen"-Toggle (wie zuvor in ProjectDescription).
function DescriptionBody({ text }: { text: string }): preact.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  if (!text) {
    return (
      <p class="mb-4 flex-1 text-sm italic text-[color:var(--color-on-surface-variant)]">
        Keine Beschreibung.
      </p>
    );
  }
  const isLong = text.length > 240 || text.split("\n").length > 3;
  return (
    <div class="mb-4 flex-1">
      <p
        class={`whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--color-on-surface)] ${
          !expanded && isLong ? "line-clamp-4" : ""
        }`}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          class="mt-2 rounded text-xs text-[color:var(--color-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
        >
          {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
        </button>
      )}
    </div>
  );
}
